import i18n from '@/i18n'
import { cloneValue, mergeValues } from './configPaths'

function contentLayoutFor(slides) {
  if ((slides || []).some((slide) => (slide.contactRows || []).length > 0)) {
    return 'contact'
  }
  if ((slides || []).some((slide) => (
    (slide.bullets || []).length > 0
    || Boolean(String(slide.information || '').trim())
    || [
      slide.kicker,
      slide.subheadline,
    ].filter((value) => Boolean(String(value || '').trim())).length > 1
  ))) {
    return 'detailed'
  }
  return 'hero'
}

function compositionId(formatId, layout, stylePackId) {
  const prefix = {
    'medium-rectangle': 'mr',
    'half-page': 'hp',
    billboard: 'bb',
  }[formatId] || 'mr'

  if (stylePackId === 'friendly-blob' && ['medium-rectangle', 'half-page'].includes(formatId)) {
    return `${prefix}-friendly-canvas`
  }
  const suffix = {
    'vertical-left': 'vertical-left',
    'vertical-right': 'vertical-right',
    'horizontal-top': 'horizontal-top',
    'horizontal-bottom': 'horizontal-bottom',
    'full-image': 'full-image',
    imageless: 'imageless',
    canvas: 'friendly-canvas',
  }[layout]
  return suffix ? `${prefix}-${suffix}` : 'custom'
}

function supportedLayout(template, formatId, requested) {
  const format = template.manifest.formats.find((entry) => entry.id === formatId)
  if (!format) return template.defaults.design.layout
  return format.layouts.includes(requested) ? requested : format.layouts[0]
}

function migrateSplitSliderV1ToV2(configuration, template) {
  const source = cloneValue(configuration)
  const next = cloneValue(template.defaults)
  const formatId = template.manifest.formats.some((entry) => entry.id === source.format?.id)
    ? source.format.id
    : next.format.id
  const format = template.manifest.formats.find((entry) => entry.id === formatId)
  const stylePackId = source.design?.stylePackId === 'friendly-blob'
    ? 'friendly-blob'
    : 'classic-split'
  const layout = supportedLayout(template, formatId, source.design?.layout)
  const imagePosition = {
    'vertical-left': 'left',
    'vertical-right': 'right',
    'horizontal-top': 'top',
    'horizontal-bottom': 'bottom',
    'full-image': 'full',
    imageless: 'none',
    canvas: 'canvas',
  }[layout] || 'left'

  next.format = {
    id: format.id,
    width: format.width,
    height: format.height,
  }
  next.ad = cloneValue(source.ad || next.ad)
  next.brand = mergeValues(next.brand, source.brand || {})
  next.brand.alignment = 'auto'
  next.brand.size = 'medium'
  next.design = mergeValues(next.design, {
    stylePackId,
    layout,
    imagePosition,
    contentLayout: contentLayoutFor(source.slides),
    readabilityOverlay: ['full-image', 'canvas'].includes(layout) ? 'auto' : 'none',
    edgeStyle: source.design?.edgeStyle || next.design.edgeStyle,
    pattern: source.design?.pattern || next.design.pattern,
    patternColor: source.design?.patternColor || next.design.patternColor,
    buttonStyle: source.design?.buttonStyle || next.design.buttonStyle,
    glow: source.design?.glow || next.design.glow,
    colors: cloneValue(source.design?.colors || next.design.colors),
    backgroundAssetId: source.design?.backgroundAssetId || null,
    backgroundFocalPoint: cloneValue(
      source.design?.backgroundFocalPoint || next.design.backgroundFocalPoint,
    ),
    promotion: cloneValue(source.design?.promotion || next.design.promotion),
  })
  next.design.compositionPresetId = compositionId(formatId, layout, stylePackId)
  next.animation = mergeValues(next.animation, source.animation || {})
  next.slides = cloneValue(source.slides || next.slides)
  next.assets = cloneValue(source.assets || {})
  next.templateSpecific = cloneValue(source.templateSpecific || {})

  return next
}

function migrateSplitSliderV2Patch(configuration, template) {
  const next = cloneValue(configuration)
  next.schemaVersion = template.defaults.schemaVersion
  next.template = cloneValue(template.defaults.template)
  return next
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value))
}

// The focus point is carried across as well as applied. Earlier releases held
// it as a field of its own and this one folds it into the crop, but the point
// itself is still worth having: it is what the crop was placed around, and
// keeping it lets the editor show it and a later change of format re-derive
// the box from the same subject.
function legacyMedia(sourceAssetId, focalPoint, assets, format) {
  if (!sourceAssetId) {
    return { sourceAssetId: null, renditionAssetId: null, crop: null }
  }

  const metadata = assets?.[sourceAssetId] || {}
  const sourceWidth = Number(metadata.width || 0)
  const sourceHeight = Number(metadata.height || 0)
  const targetWidth = Number(format?.width || 0)
  const targetHeight = Number(format?.height || 0)
  let crop = null

  if (sourceWidth > 0 && sourceHeight > 0 && targetWidth > 0 && targetHeight > 0) {
    const sourceRatio = sourceWidth / sourceHeight
    const targetRatio = targetWidth / targetHeight
    const focusX = Number(focalPoint?.x ?? 50)
    const focusY = Number(focalPoint?.y ?? 50)
    if (sourceRatio > targetRatio) {
      const width = targetRatio / sourceRatio * 100
      crop = {
        x: clamp(focusX - width / 2, 0, 100 - width),
        y: 0,
        width,
        height: 100,
      }
    } else {
      const height = sourceRatio / targetRatio * 100
      crop = {
        x: 0,
        y: clamp(focusY - height / 2, 0, 100 - height),
        width: 100,
        height,
      }
    }
  }

  const carried = focalPoint
    && Number.isFinite(Number(focalPoint.x))
    && Number.isFinite(Number(focalPoint.y))
    ? { x: clamp(Number(focalPoint.x), 0, 100), y: clamp(Number(focalPoint.y), 0, 100) }
    : null

  return {
    sourceAssetId,
    renditionAssetId: null,
    crop,
    ...(carried ? { focalPoint: carried } : {}),
  }
}

function migrateSplitSliderLegacyToV3(configuration, template) {
  const source = cloneValue(configuration)
  const next = cloneValue(template.defaults)
  const formatId = template.manifest.formats.some((entry) => entry.id === source.format?.id)
    ? source.format.id
    : next.format.id
  const format = template.manifest.formats.find((entry) => entry.id === formatId)
  const stylePackId = source.design?.stylePackId === 'friendly-blob'
    ? 'friendly-blob'
    : 'classic-split'
  const layout = supportedLayout(template, formatId, source.design?.layout)
  const assets = cloneValue(source.assets || {})

  next.format = { id: format.id, width: format.width, height: format.height }
  next.ad = cloneValue(source.ad || next.ad)
  next.brand = mergeValues(next.brand, {
    name: source.brand?.name ?? next.brand.name,
    subtitle: source.brand?.subtitle ?? next.brand.subtitle,
    logoMode: source.brand?.logoMode ?? next.brand.logoMode,
    identityTheme: source.brand?.identityTheme ?? next.brand.identityTheme,
    alignment: source.brand?.alignment ?? 'auto',
    size: source.brand?.size ?? 'medium',
  })
  next.brand.logoMedia = legacyMedia(
    source.brand?.logoAssetId,
    { x: 50, y: 50 },
    assets,
    { width: 138, height: 24 },
  )
  next.brand.logoMedia.crop = null
  next.brand.faviconMedia = legacyMedia(
    source.brand?.faviconAssetId,
    { x: 50, y: 50 },
    assets,
    { width: 40, height: 40 },
  )

  next.design = mergeValues(next.design, {
    stylePackId,
    layout,
    imagePosition: {
      'vertical-left': 'left',
      'vertical-right': 'right',
      'horizontal-top': 'top',
      'horizontal-bottom': 'bottom',
      'full-image': 'full',
      imageless: 'none',
      canvas: 'canvas',
    }[layout] || 'left',
    contentLayout: source.design?.contentLayout || contentLayoutFor(source.slides),
    readabilityOverlay: source.design?.readabilityOverlay
      || (['full-image', 'canvas'].includes(layout) ? 'auto' : 'none'),
    edgeStyle: source.design?.edgeStyle || next.design.edgeStyle,
    pattern: source.design?.pattern || next.design.pattern,
    patternColor: source.design?.patternColor || next.design.patternColor,
    buttonStyle: source.design?.buttonStyle || next.design.buttonStyle,
    glow: source.design?.glow || next.design.glow,
    colors: cloneValue(source.design?.colors || next.design.colors),
    promotion: cloneValue(source.design?.promotion || next.design.promotion),
  })
  next.design.backgroundMedia = legacyMedia(
    source.design?.backgroundAssetId,
    source.design?.backgroundFocalPoint,
    assets,
    format,
  )
  next.design.compositionPresetId = compositionId(formatId, layout, stylePackId)
  next.animation = mergeValues(next.animation, source.animation || {})
  next.slides = (source.slides || []).map((slide, index) => ({
    ...cloneValue(next.slides[index] || next.slides[0]),
    ...cloneValue(slide),
    media: legacyMedia(
      slide.imageAssetId,
      slide.focalPoint,
      assets,
      format,
    ),
    imageAssetId: undefined,
    focalPoint: undefined,
  }))
  next.slides = next.slides.map((slide) => Object.fromEntries(
    Object.entries(slide).filter(([, value]) => value !== undefined),
  ))
  next.assets = assets
  next.templateSpecific = cloneValue(source.templateSpecific || {})
  next.schemaVersion = template.defaults.schemaVersion
  next.template = cloneValue(template.defaults.template)

  return next
}

// Fills a same-shape release from its defaults: every key the target declares is taken from
// the source when present (collections whole), unknown source keys are dropped so the server's
// unknown-key protection cannot reject the result, and the template identity is retargeted.
// Used when a new release only adds presentation metadata or new optional fields.
function mergeDefaults(defaults, source) {
  if (Array.isArray(defaults)) {
    return Array.isArray(source) ? cloneValue(source) : cloneValue(defaults)
  }
  if (defaults && typeof defaults === 'object') {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return cloneValue(defaults)
    const next = {}
    for (const key of Object.keys(defaults)) {
      next[key] = key in source ? mergeDefaults(defaults[key], source[key]) : cloneValue(defaults[key])
    }
    return next
  }
  return source === undefined ? cloneValue(defaults) : cloneValue(source)
}

function migrateSameShape(configuration, template) {
  const source = cloneValue(configuration)
  const next = mergeDefaults(template.defaults, source)
  next.assets = cloneValue(source.assets || {})
  next.schemaVersion = template.defaults.schemaVersion
  next.template = cloneValue(template.defaults.template)
  if (source.format && template.manifest.formats.some((entry) => entry.id === source.format.id)) {
    const format = template.manifest.formats.find((entry) => entry.id === source.format.id)
    next.format = { ...next.format, ...source.format, id: format.id, width: format.width, height: format.height }
    if (format.bannerFormatAlias) next.format.bannerFormatAlias = format.bannerFormatAlias
  }
  return next
}

function escapeHtml(value) {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// 3.2.0 keeps the 3.1.1 data model and adds the AD-209 editor presentation. Its slogan markup is
// a superset of 3.1.1's; button labels become rich text, so 3.1.1's plain labels are escaped.
// It allows at most three slides and never drops one silently. Unlike migrateSameShape it keeps
// the editor metadata (generation suggestions, campaign choices) and the ad's identity.
export function migrateSplitSlider311To320(configuration, template) {
  const slides = configuration?.data?.slides || []
  const collection = (template.schema?.collections || []).find((entry) => entry.path === 'data.slides')
  const maximum = collection?.maxItems ?? 3
  if (slides.length > maximum) {
    const extra = slides.length - maximum
    throw new Error(i18n.t('editorV2.core.errors.tooManySlides', { count: extra, slides: slides.length, maximum }))
  }
  const next = migrateSameShape(configuration, template)
  next.data.slides = (next.data.slides || []).map((slide) => ({
    ...slide,
    cta: { ...(slide.cta || {}), text: escapeHtml(slide.cta?.text) },
  }))
  for (const key of ['id', 'revision', 'ownerUserId', 'suggestions', 'campaign']) {
    if (configuration?.[key] !== undefined) next[key] = cloneValue(configuration[key])
  }
  return next
}

// 3.2.1 changes only where the bubble docks and its default size. Same shape; the editor metadata
// and identity travel as in 3.1.1 → 3.2.0, and a bubble still at the old default (62) takes the
// Figma size (42) — a size somebody picked stays.
export function migrateSplitSlider320To321(configuration, template) {
  const next = migrateSameShape(configuration, template)
  for (const key of ['id', 'revision', 'ownerUserId', 'suggestions', 'campaign']) {
    if (configuration?.[key] !== undefined) next[key] = cloneValue(configuration[key])
  }
  const bubble = next.data?.style?.elements?.bubble
  if (bubble && bubble.size === 62) bubble.size = template.defaults?.data?.style?.elements?.bubble?.size ?? 42
  return next
}

// split-slider 3.3.0, story 1.1.0 and reel 1.1.0 replace the four Font Style faces with seven web
// fonts. Poppins, the default, is the only face both lists share, so every old choice becomes it.
export function migrateWebFonts(configuration, template) {
  const next = migrateSameShape(configuration, template)
  for (const key of ['id', 'revision', 'ownerUserId', 'suggestions', 'campaign']) {
    if (configuration?.[key] !== undefined) next[key] = cloneValue(configuration[key])
  }
  const slogan = next.data?.style?.elements?.slogan
  if (slogan) slogan.font = template.defaults.data.style.elements.slogan.font
  return next
}

const EDGES_330 = { wide: 'slant', soft: 'rounded', capsule: 'rounded', cut: 'straight', zigzag: 'chevron' }
const PATTERN_BACKGROUNDS_330 = {
  none: 'plainbg', pinstripe: 'pinstripe', bold: 'pinstripe', vertical: 'pinstripe', diagonal: 'slice', chevron: 'slice',
  mesh: 'mesh', carbon: 'mesh', grid: 'studio', dots: 'sunrise', halftone: 'terrace',
}

export function migrateSplitSlider321To330(configuration, template) {
  const next = migrateWebFonts(configuration, template)
  const surface = next.data?.style?.surface
  if (surface) {
    const pattern = configuration?.data?.style?.surface?.pattern
    surface.edge = next.data?.layout === 'full-image' ? 'fullscreen' : EDGES_330[surface.edge] || surface.edge
    surface.background = PATTERN_BACKGROUNDS_330[pattern] || template.defaults?.data?.style?.surface?.background || 'plainbg'
    surface.pattern = 'none'
    surface.panelShape = 'rect'
  }
  return next
}

// story 1.2.0 drops Scrim and Photo grade and adds the picked Ad Studio layouts (data.layouts,
// from the defaults). The Font Style is kept, unlike migrateWebFonts.
export function migrateStory110To120(configuration, template) {
  const next = migrateSameShape(configuration, template)
  for (const key of ['id', 'revision', 'ownerUserId', 'suggestions', 'campaign']) {
    if (configuration?.[key] !== undefined) next[key] = cloneValue(configuration[key])
  }
  return next
}

// Same shape plus the logo margin and favicon background, which come from the defaults.
export function migrateIdentityOptions(configuration, template) {
  const next = migrateSameShape(configuration, template)
  for (const key of ['id', 'revision', 'ownerUserId', 'suggestions', 'campaign']) {
    if (configuration?.[key] !== undefined) next[key] = cloneValue(configuration[key])
  }
  return next
}

// story 1.5.0: Frame is gone (closest: Panel), Pattern becomes a Background, the bubble size a step.
export function migrateStory140To150(configuration, template) {
  const next = migrateIdentityOptions(configuration, template)
  const style = next.data?.style
  const composition = style?.composition
  if (composition && !['free', 'lines', 'panel', 'band'].includes(composition.copyBlock)) composition.copyBlock = 'panel'
  if (style?.surface) {
    style.surface.background = PATTERN_BACKGROUNDS_330[configuration?.data?.style?.surface?.pattern] || 'plainbg'
    style.surface.pattern = 'none'
  }
  const bubble = style?.elements?.bubble
  const size = Number(configuration?.data?.style?.elements?.bubble?.size)
  if (bubble && Number.isFinite(size)) bubble.scale = size < 50 ? 's' : size < 70 ? 'm' : 'l'
  return next
}

// split-slider 3.7.0, story 1.6.0 and reel 1.4.0: a favicon sits left or right only.
function faviconSide(migrate) {
  return (configuration, template) => {
    const next = migrate(configuration, template)
    const brand = next.data?.brand
    if (brand?.mode === 'favicon' && (brand.alignment === 'auto' || brand.alignment === 'center')) brand.alignment = 'left'
    return next
  }
}

// split-slider 3.6.0: the logo's white plate becomes a switch, off for new ads as in Ad Studio; an upgraded
// ad keeps it on so it looks as before. The bubble size becomes Small / Medium / Big, from the nearest step.
export function migrateSplitSlider350To360(configuration, template) {
  const next = migrateIdentityOptions(configuration, template)
  if (next.data?.brand) next.data.brand.logoPlate = true
  const bubble = next.data?.style?.elements?.bubble
  const size = Number(configuration?.data?.style?.elements?.bubble?.size)
  if (bubble && Number.isFinite(size)) bubble.scale = size < 40 ? 's' : size < 50 ? 'm' : 'l'
  return next
}

const migrations = {
  'split-slider@1.0.0->split-slider@2.0.0': migrateSplitSliderV1ToV2,
  'split-slider@1.0.0->split-slider@2.0.1': migrateSplitSliderV1ToV2,
  'split-slider@1.0.0->split-slider@2.1.0': migrateSplitSliderV1ToV2,
  'split-slider@2.0.0->split-slider@2.0.1': migrateSplitSliderV2Patch,
  'split-slider@2.0.0->split-slider@2.1.0': migrateSplitSliderV2Patch,
  'split-slider@2.0.1->split-slider@2.1.0': migrateSplitSliderV2Patch,
  'split-slider@1.0.0->split-slider@2.2.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.0.0->split-slider@2.2.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.0.1->split-slider@2.2.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.1.0->split-slider@2.2.0': migrateSplitSliderLegacyToV3,
  // 2.3.0 keeps the 2.2.0 configuration shape and adds design.fontFamily (filled from defaults).
  'split-slider@1.0.0->split-slider@2.3.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.0.0->split-slider@2.3.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.0.1->split-slider@2.3.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.1.0->split-slider@2.3.0': migrateSplitSliderLegacyToV3,
  'split-slider@2.2.0->split-slider@2.3.0': migrateSameShape,
  // scratch-to-reveal 1.0.2 only adds the schema presentation layer.
  'scratch-to-reveal@1.0.0->scratch-to-reveal@1.0.2': migrateSameShape,
  'scratch-to-reveal@1.0.1->scratch-to-reveal@1.0.2': migrateSameShape,
  // AD-209 editor presentation, rich slogan/CTA text, three slides.
  'split-slider@3.1.1->split-slider@3.2.0': migrateSplitSlider311To320,
  // The bubble docks opposite the identity; Figma bubble size.
  'split-slider@3.2.0->split-slider@3.2.1': migrateSplitSlider320To321,
  // AD-242: Ad Studio's types and shapes, Backgrounds instead of Pattern, intro; seven web fonts.
  'split-slider@3.2.1->split-slider@3.3.0': migrateSplitSlider321To330,
  'split-slider@3.2.0->split-slider@3.3.0': (configuration, template) => migrateSplitSlider321To330(migrateSplitSlider320To321(configuration, template), template),
  'story@1.0.0->story@1.1.0': migrateWebFonts,
  // Ad Studio's Story layouts; no Scrim or Photo grade.
  'story@1.1.0->story@1.2.0': migrateStory110To120,
  'story@1.0.0->story@1.2.0': (configuration, template) => migrateStory110To120(migrateWebFonts(configuration, template), template),
  'reel@1.0.0->reel@1.1.0': migrateWebFonts,
  // Logo margin and favicon background.
  'split-slider@3.3.0->split-slider@3.4.0': migrateIdentityOptions,
  'split-slider@3.2.1->split-slider@3.4.0': (configuration, template) => migrateIdentityOptions(migrateSplitSlider321To330(configuration, template), template),
  'split-slider@3.2.0->split-slider@3.4.0': (configuration, template) => migrateIdentityOptions(migrateSplitSlider321To330(migrateSplitSlider320To321(configuration, template), template), template),
  'story@1.2.0->story@1.3.0': migrateIdentityOptions,
  'story@1.1.0->story@1.3.0': (configuration, template) => migrateIdentityOptions(migrateStory110To120(configuration, template), template),
  'story@1.0.0->story@1.3.0': (configuration, template) => migrateIdentityOptions(migrateStory110To120(migrateWebFonts(configuration, template), template), template),
  'reel@1.1.0->reel@1.2.0': migrateIdentityOptions,
  'reel@1.0.0->reel@1.2.0': (configuration, template) => migrateIdentityOptions(migrateWebFonts(configuration, template), template),
  'swipe-ad@1.0.0->swipe-ad@1.1.0': migrateIdentityOptions,
  'scratch-to-reveal@1.0.0->scratch-to-reveal@1.0.3': migrateIdentityOptions,
  'scratch-to-reveal@1.0.1->scratch-to-reveal@1.0.3': migrateIdentityOptions,
  'scratch-to-reveal@1.0.2->scratch-to-reveal@1.0.3': migrateIdentityOptions,
  // Host hooks for the widget's tracking (onInteraction, onClickout): the configuration keeps its shape.
  'split-slider@3.4.0->split-slider@3.5.0': migrateIdentityOptions,
  'split-slider@3.3.0->split-slider@3.5.0': migrateIdentityOptions,
  'split-slider@3.2.1->split-slider@3.5.0': (configuration, template) => migrateIdentityOptions(migrateSplitSlider321To330(configuration, template), template),
  'split-slider@3.2.0->split-slider@3.5.0': (configuration, template) => migrateIdentityOptions(migrateSplitSlider321To330(migrateSplitSlider320To321(configuration, template), template), template),
  // Logo without the white plate, bubble size Small / Medium / Big (Simon's Loom).
  'split-slider@3.5.0->split-slider@3.6.0': migrateSplitSlider350To360,
  'split-slider@3.4.0->split-slider@3.6.0': migrateSplitSlider350To360,
  'split-slider@3.3.0->split-slider@3.6.0': migrateSplitSlider350To360,
  'split-slider@3.2.1->split-slider@3.6.0': (configuration, template) => migrateSplitSlider350To360(migrateSplitSlider321To330(configuration, template), template),
  'split-slider@3.2.0->split-slider@3.6.0': (configuration, template) => migrateSplitSlider350To360(migrateSplitSlider321To330(migrateSplitSlider320To321(configuration, template), template), template),
  // Ad Studio's remaining templates only add options; a favicon on Auto or Center moves to the left.
  'split-slider@3.6.0->split-slider@3.7.0': faviconSide(migrateIdentityOptions),
  'split-slider@3.5.0->split-slider@3.7.0': faviconSide(migrateSplitSlider350To360),
  'split-slider@3.4.0->split-slider@3.7.0': faviconSide(migrateSplitSlider350To360),
  'split-slider@3.3.0->split-slider@3.7.0': faviconSide(migrateSplitSlider350To360),
  'split-slider@3.2.1->split-slider@3.7.0': faviconSide((configuration, template) => migrateSplitSlider350To360(migrateSplitSlider321To330(configuration, template), template)),
  'split-slider@3.2.0->split-slider@3.7.0': faviconSide((configuration, template) => migrateSplitSlider350To360(migrateSplitSlider321To330(migrateSplitSlider320To321(configuration, template), template), template)),
  'story@1.3.0->story@1.4.0': migrateIdentityOptions,
  'story@1.2.0->story@1.4.0': migrateIdentityOptions,
  'story@1.1.0->story@1.4.0': (configuration, template) => migrateIdentityOptions(migrateStory110To120(configuration, template), template),
  'story@1.0.0->story@1.4.0': (configuration, template) => migrateIdentityOptions(migrateStory110To120(migrateWebFonts(configuration, template), template), template),
  'story@1.4.0->story@1.5.0': migrateStory140To150,
  'story@1.3.0->story@1.5.0': migrateStory140To150,
  'story@1.2.0->story@1.5.0': migrateStory140To150,
  'story@1.1.0->story@1.5.0': (configuration, template) => migrateStory140To150(migrateStory110To120(configuration, template), template),
  'story@1.0.0->story@1.5.0': (configuration, template) => migrateStory140To150(migrateStory110To120(migrateWebFonts(configuration, template), template), template),
  'story@1.5.0->story@1.6.0': faviconSide(migrateIdentityOptions),
  'story@1.4.0->story@1.6.0': faviconSide(migrateStory140To150),
  'story@1.3.0->story@1.6.0': faviconSide(migrateStory140To150),
  'story@1.2.0->story@1.6.0': faviconSide(migrateStory140To150),
  'story@1.1.0->story@1.6.0': faviconSide((configuration, template) => migrateStory140To150(migrateStory110To120(configuration, template), template)),
  'story@1.0.0->story@1.6.0': faviconSide((configuration, template) => migrateStory140To150(migrateStory110To120(migrateWebFonts(configuration, template), template), template)),
  'reel@1.2.0->reel@1.3.0': migrateIdentityOptions,
  'reel@1.1.0->reel@1.3.0': migrateIdentityOptions,
  'reel@1.0.0->reel@1.3.0': (configuration, template) => migrateIdentityOptions(migrateWebFonts(configuration, template), template),
  'reel@1.3.0->reel@1.4.0': faviconSide(migrateIdentityOptions),
  'reel@1.2.0->reel@1.4.0': faviconSide(migrateIdentityOptions),
  'reel@1.1.0->reel@1.4.0': faviconSide(migrateIdentityOptions),
  'reel@1.0.0->reel@1.4.0': faviconSide((configuration, template) => migrateIdentityOptions(migrateWebFonts(configuration, template), template)),
  'swipe-ad@1.1.0->swipe-ad@1.2.0': migrateIdentityOptions,
  'swipe-ad@1.0.0->swipe-ad@1.2.0': migrateIdentityOptions,
  'scratch-to-reveal@1.0.3->scratch-to-reveal@1.1.0': migrateIdentityOptions,
  'scratch-to-reveal@1.0.2->scratch-to-reveal@1.1.0': migrateIdentityOptions,
  'scratch-to-reveal@1.0.1->scratch-to-reveal@1.1.0': migrateIdentityOptions,
  'scratch-to-reveal@1.0.0->scratch-to-reveal@1.1.0': migrateIdentityOptions,
}

export function migrateConfiguration(configuration, sourceRelease, targetTemplate) {
  const targetRelease = targetTemplate?.manifest?.releaseKey
  const migrate = migrations[`${sourceRelease}->${targetRelease}`]
  if (!migrate) {
    throw new Error(i18n.t('editorV2.core.errors.noMigration', { source: sourceRelease, target: targetRelease }))
  }
  return migrate(configuration, targetTemplate)
}
